import type { QueryClient } from '@tanstack/react-query';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';

import { scheduleKeys } from './schedule-keys';

/**
 * A pool or target change creates or releases draft store products, so each target store's
 * catalog refetches. The key is spelled here rather than taken from the store-catalog door, so the
 * store catalog can later import schedules (it shows their badges) without creating a cycle.
 */
function invalidateStoreCatalogs(queryClient: QueryClient, storeIds: readonly string[]) {
  return Promise.all(
    storeIds.map((storeId) =>
      queryClient.invalidateQueries({ queryKey: ['stores', storeId, 'catalog'] }),
    ),
  );
}

/** Puts a fresh schedule in the cache and refreshes everything that reads schedules. */
export function rememberSchedule(
  queryClient: QueryClient,
  schedule: ProductSchedule,
  touchedStoreIds: readonly string[] = [],
) {
  queryClient.setQueryData(scheduleKeys.detail(schedule.id), schedule);
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: scheduleKeys.all,
      predicate: (query) => {
        const key = query.queryKey;
        // The detail itself was just written; its sub-queries (pool, runs) still refetch.
        return !(key.length === 4 && key[2] === 'detail' && key[3] === schedule.id);
      },
    }),
    invalidateStoreCatalogs(queryClient, [
      ...new Set([...schedule.stores.map((s) => s.storeId), ...touchedStoreIds]),
    ]),
  ]);
}

/** After a pool change: the schedule's counts, its pool, its runs and the target catalogs. */
export function refreshSchedule(queryClient: QueryClient, id: string) {
  const cached = queryClient.getQueryData<ProductSchedule>(scheduleKeys.detail(id));
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(id) }),
    queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() }),
    invalidateStoreCatalogs(queryClient, cached?.stores.map((s) => s.storeId) ?? []),
  ]);
}

/**
 * A run finished on the worker (`runningRunId` went from set to null): what it changed shows in
 * the runs and their items, the pool's placements, the timeline, the schedule lists and every
 * target store's catalog. The detail itself was just fetched, so it is left alone.
 */
export function refreshAfterRun(queryClient: QueryClient, schedule: ProductSchedule) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: scheduleKeys.runs(schedule.id) }),
    queryClient.invalidateQueries({ queryKey: scheduleKeys.members(schedule.id) }),
    queryClient.invalidateQueries({ queryKey: scheduleKeys.timelines() }),
    queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() }),
    invalidateStoreCatalogs(
      queryClient,
      schedule.stores.map((s) => s.storeId),
    ),
  ]);
}
